import React, { useCallback, useEffect, useMemo, useState } from "react";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { push, replace } from "react-router-redux";

import { Button, TransferTicket, resolveTransferTitleKey, transfersTestIds } from "@ui-kit/components/index.es";
import { resizableRoute } from "pages/_components/Resizable";
import ModifyPendingTransactionModal from "pages/transactions/_components/ModifyPendingTransactionModal";
import TransferSummaryData from "pages/transfer/TransferSummaryData";

import { actions as formActions } from "reducers/form";
import { actions as layoutActions } from "reducers/layout";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as statusSelectors } from "reducers/status";
import { actions as transactionsActions, selectors as transactionsSelectors } from "reducers/transactions";
import { actions as transferActions, selectors as transferSelectors } from "reducers/transfer";
import {
    actions as frequentDestinationActions,
    selectors as frequentDestinationSelectors,
} from "reducers/frequentDestination";

import * as config from "util/config";
import resolveDraftTransactionListGoBack from "util/draftTransactionListGoBack";
import formatLegacyWebDateTimeString from "util/formatLegacyWebDateTimeString";
import * as i18n from "util/i18n";
import {
    canUserSignPendingFromPotentialSchemes,
    exportGenericTicketPdfWithSuccessMessage,
    isMvp3PendingTransactionsEnabled,
    isTransactionPendingProcessing,
    shareMobileGenericTicketPdf,
} from "util/transaction";
import { getTransferUxLabel, isTransferUxI18nKey } from "util/transferInternalTitleLabels";
import { resolveTransferFormResetGoBackPath } from "util/transfers";
import { dispatchV3ShellLayout, isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { getPostLoginHomePath, getTransferConfirmationPath, getTransferTicketPath } from "util/v3UxConfig";

import {
    buildTransferDataInput,
    buildTransferTicketPdfBody,
    getTadNote,
    getTransferTicketNotes,
} from "./transferTicketData";

/** Only these transfer types can be shared as a PDF from mobile. */
const ACTIVITIES_WITH_SHARE_BUTTON = [
    "transfers.toContact.send",
    "transfers.thirdParties.send",
    "transfersCVU.toContact.send",
    "transfersCVU.thirdParties.send",
];

const TRANSFER_TICKET_SCOPES = ["pendingTransfer"];

/** Product id from summary account (object or bare idProduct string after readTransaction). */
function resolveAccountProductId(account) {
    if (!account) {
        return null;
    }
    if (typeof account === "string") {
        return account;
    }
    return account.idProduct || account.value || null;
}

/** CBU/CVU ids are 22 digits — accounts.info cannot resolve them as products. */
function isCbuOrCvuProductId(id) {
    return typeof id === "string" && /^\d{22}$/.test(id);
}

/**
 * After readTransaction, debit/credit are often bare idProduct strings. Prefer the in-memory
 * accounts list from pre; otherwise ask accounts.info (same as legacy TransferTransactionData).
 */
function resolveAccountFromListOrId(account, accounts) {
    if (!account) {
        return account;
    }
    if (account.number != null && String(account.number).length > 0) {
        return account;
    }
    const productId = resolveAccountProductId(account);
    if (!productId || isCbuOrCvuProductId(productId) || !Array.isArray(accounts)) {
        return account;
    }
    return accounts.find((item) => item.idProduct === productId) || account;
}

/**
 * V3 transfer receipt. A single route serves every status (`FINISHED`, `PENDING`, `FAILED`,
 * `CANCELLED`, `SCHEDULED`) because the UI-kit `TransferTicket` derives the banner from
 * `idTransactionStatus`; legacy instead used one route per status.
 */
function TransferTicketV3Page(props) {
    const {
        dispatch,
        summary,
        accounts,
        internalTransfer,
        transferGroup,
        scheduledCancel,
        isCVU,
        activeEnvironment,
        fullName,
        lastHrefPathname,
        transactionRoute,
        isDesktop,
        loggedUser,
        potentialSignaturesData,
        administrationScheme,
        frequentDestination,
        idEnvironment,
    } = props;

    const [isModalDisplayed, setIsModalDisplayed] = useState(false);
    const [exportPdfLoading, setExportPdfLoading] = useState(false);
    const [signaturesDrawerOpenMobile, setSignaturesDrawerOpenMobile] = useState(false);

    const status = summary?.idTransactionStatus;
    const isPending = status === "PENDING" || status === "PENDING_TOKEN";
    const isFinished = status === "FINISHED";
    const isScheduled = status === "SCHEDULED";

    // Ticket loads transaction.data with idProduct strings; hydrate product objects for labels.
    const displaySummary = useMemo(() => {
        if (!summary) {
            return summary;
        }
        return {
            ...summary,
            debitAccount: resolveAccountFromListOrId(summary.debitAccount, accounts),
            creditAccount: resolveAccountFromListOrId(summary.creditAccount, accounts),
            reference: summary.reference ?? summary.creditReference ?? null,
        };
    }, [summary, accounts]);

    useEffect(() => {
        const beneficiaryCbu = displaySummary?.creditAccountDetail?.cbu;
        const hasPermission = activeEnvironment?.permissions?.frequentDestination;
        if (beneficiaryCbu && hasPermission && idEnvironment) {
            dispatch(frequentDestinationActions.getFrequentDestinationRequest(idEnvironment, beneficiaryCbu));
        }
    }, [dispatch, displaySummary?.creditAccountDetail?.cbu, activeEnvironment, idEnvironment]);

    /**
     * Opens V2 agenda create (`/v2/agenda/create`) with the ticket beneficiary CBU/alias preloaded.
     */
    const handleCreateFrequentDestination = useCallback(() => {
        const cbu = displaySummary?.cbu ?? displaySummary?.creditAccountDetail?.cbu;
        const alias = displaySummary?.alias;
        let cbuAlias = "";
        if (cbu) {
            cbuAlias = cbu;
        }
        if (alias) {
            cbuAlias = alias;
        }
        dispatch(frequentDestinationActions.setFrequentDestination({ cbu: cbuAlias }));
        dispatch(push("/v2/agenda/create"));
    }, [dispatch, displaySummary]);

    const showFrequentDestinationButton = useMemo(() => {
        if (internalTransfer || scheduledCancel) {
            return false;
        }
        const cbu = displaySummary?.cbu ?? displaySummary?.creditAccountDetail?.cbu;
        const alias = displaySummary?.alias;
        const hasPermission = activeEnvironment?.permissions?.frequentDestination;
        const isAlreadySaved = Boolean(frequentDestination?.cbu && frequentDestination.cbu === cbu);
        return Boolean(hasPermission && !isAlreadySaved && (cbu || alias));
    }, [activeEnvironment, displaySummary, frequentDestination, internalTransfer, scheduledCancel]);

    useEffect(() => {
        if (!summary?.idTransaction) {
            dispatch(replace("/transactions/list"));
            return;
        }
        dispatch(transactionsActions.getPotentialSignaturesData(summary.idTransaction));
    }, [dispatch, summary?.idTransaction]);

    useEffect(() => {
        if (!displaySummary) {
            return;
        }
        const { debitAccount, creditAccount } = displaySummary;
        const debitNeedsInfo = debitAccount && debitAccount.number == null;
        const debitId = resolveAccountProductId(debitAccount);
        // takeLatest on GET_ACCOUNT_INFO: request one account at a time.
        if (debitNeedsInfo && debitId && !isCbuOrCvuProductId(debitId)) {
            dispatch(transferActions.getAccountInfo(debitId, "debit", true));
            return;
        }
        if (!internalTransfer) {
            return;
        }
        const creditNeedsInfo = creditAccount && creditAccount.number == null;
        const creditId = resolveAccountProductId(creditAccount);
        if (creditNeedsInfo && creditId && !isCbuOrCvuProductId(creditId)) {
            dispatch(transferActions.getAccountInfo(creditId, "credit", true));
        }
    }, [dispatch, displaySummary, internalTransfer]);

    const getLabels = useCallback((id) => i18n.get(id), []);

    const transferData = useMemo(
        () =>
            buildTransferDataInput({
                summary: displaySummary,
                internalTransfer,
                scheduledCancel,
                isCVU,
                activeEnvironment,
            }),
        [displaySummary, internalTransfer, scheduledCancel, isCVU, activeEnvironment],
    );

    const tadNote = useMemo(() => getTadNote(displaySummary, internalTransfer), [displaySummary, internalTransfer]);

    const ticketNotes = useMemo(() => getTransferTicketNotes(displaySummary, internalTransfer), [
        displaySummary,
        internalTransfer,
    ]);

    /** Same title the UI-kit prints on the receipt banner; reused for the mobile header and the PDF. */
    const ticketTitle = useMemo(() => {
        const titleKey = resolveTransferTitleKey(displaySummary?.idActivity);
        if (isTransferUxI18nKey(titleKey, "transfers.summary.internal.label")) {
            return getTransferUxLabel("transfers.summary.internal.label", "Transferir a mis cuentas");
        }
        return i18n.get(titleKey);
    }, [displaySummary]);

    /** Mobile signature control banner (legacy TransactionTicket hint). */
    const titleHintText = useMemo(() => {
        if (!isPending) {
            return undefined;
        }
        const requiresMobileSignatureControl = displaySummary?.control;
        if (!requiresMobileSignatureControl) {
            return undefined;
        }
        const text = config.get("frontend.mobileSignatureControl.text", "");
        return text || undefined;
    }, [displaySummary?.control, isPending]);

    const handleDownload = useCallback(
        async (share = false) => {
            if (!displaySummary) {
                return;
            }
            setExportPdfLoading(true);
            try {
                const ticketData = {
                    ticketName: ticketTitle,
                    ticketBody: buildTransferTicketPdfBody({
                        summary: displaySummary,
                        internalTransfer,
                        scheduledCancel,
                        isCVU,
                        activeEnvironment,
                    }),
                    ticketMessage: tadNote ?? undefined,
                };
                if (share) {
                    await shareMobileGenericTicketPdf(ticketData, dispatch, TRANSFER_TICKET_SCOPES);
                } else {
                    await exportGenericTicketPdfWithSuccessMessage(
                        ticketData,
                        dispatch,
                        i18n.get("global.download.success"),
                    );
                }
            } finally {
                setExportPdfLoading(false);
            }
        },
        [dispatch, displaySummary, internalTransfer, scheduledCancel, isCVU, activeEnvironment, tadNote, ticketTitle],
    );

    /**
     * The receipt is reachable from the creation form, the transfers history and the pending
     * authorizations list, so back returns to the actual origin instead of a fixed screen.
     */
    const goBackPath = useMemo(() => {
        if (displaySummary?.returnToPending) {
            return (
                displaySummary.draftTransactionListGoBackPath || resolveDraftTransactionListGoBack("", transactionRoute)
            );
        }
        if (displaySummary?.newTransaction) {
            return resolveTransferFormResetGoBackPath({ internalTransfer, transferGroup });
        }
        return resolveDraftTransactionListGoBack(lastHrefPathname, transactionRoute) || "/transactions/list";
    }, [displaySummary, internalTransfer, transferGroup, lastHrefPathname, transactionRoute]);

    const handleGoBack = useCallback(() => {
        const navigate = displaySummary?.newTransaction ? replace : push;
        dispatch(navigate(goBackPath));
    }, [dispatch, goBackPath, displaySummary]);

    const canShareOnMobile = useMemo(
        () =>
            Boolean(displaySummary?.newTransaction) &&
            config.getBoolean("frontend.show.transfer.shareMobile.receipt.functionalities") &&
            ACTIVITIES_WITH_SHARE_BUTTON.includes(displaySummary?.idActivity),
        [displaySummary],
    );

    const rightContentMobile = useMemo(() => {
        if (isPending) {
            return (
                <Button
                    data-testid={transfersTestIds.ticket.schemes}
                    aria-label={i18n.get("pendingTransaction.info.scheme")}
                    variant="text"
                    color="white"
                    icon="Eye"
                    iconVariant="outline"
                    onClick={() => setSignaturesDrawerOpenMobile(true)}
                />
            );
        }
        if (!isFinished) {
            return null;
        }
        return (
            <Button
                data-testid={canShareOnMobile ? transfersTestIds.ticket.share : transfersTestIds.ticket.download}
                aria-label={i18n.get(canShareOnMobile ? "global.share" : "global.download.ticket")}
                variant="text"
                color="white"
                icon="SendDown"
                iconVariant="outline"
                onClick={() => handleDownload(canShareOnMobile)}
            />
        );
    }, [isPending, isFinished, canShareOnMobile, handleDownload]);

    useEffect(() => {
        if (!isUxDashboardImprovementsEnabled()) {
            return undefined;
        }
        dispatchV3ShellLayout(dispatch, {
            headerTitle: ticketTitle,
            titleKey: "",
            goBackPath,
            scopes: TRANSFER_TICKET_SCOPES,
            rightContentMobile,
        });
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        };
    }, [dispatch, ticketTitle, goBackPath, rightContentMobile]);

    const preparedBy = useMemo(
        () => ({
            userFullName: displaySummary?.creatorFullName || fullName,
            creationDateTime: isPending ? undefined : transferData.creationDate ?? undefined,
        }),
        [displaySummary, fullName, isPending, transferData.creationDate],
    );

    const signedBy = useMemo(
        () =>
            (displaySummary?.signatures ?? []).map((s) => ({
                userFirstName: s.userFirstName,
                userLastName: s.userLastName,
                userFullName: s.userFullName,
                creationDateTime: s.creationDateTime ?? s.modificationDateTime ?? s.submitDateTime ?? s.creationDate,
            })),
        [displaySummary],
    );

    const resolveTicketPath = useCallback(() => {
        const ticketStatus = displaySummary?.idTransactionStatus || "PENDING";
        return getTransferTicketPath(ticketStatus);
    }, [displaySummary]);

    const loadTransferForAction = useCallback(
        (action) => {
            if (!displaySummary) {
                return;
            }
            // Always persist `action` (sign|cancel). Matching only exact `*.send` ids silently
            // no-oped for TAD (`*.tad.send`) and left a prior `action: "sign"` in place — cancel
            // then looked/behaved like sign on confirmation V3.
            const payload = { ...displaySummary, action };
            if (internalTransfer) {
                dispatch(transferActions.loadInternalTransfer(payload));
            } else {
                dispatch(transferActions.loadExternalTransfer(payload));
            }
        },
        [dispatch, displaySummary, internalTransfer],
    );

    const signTransaction = useCallback(() => {
        if (!displaySummary) {
            return;
        }
        const { idActivity } = displaySummary;
        const ticketPath = resolveTicketPath();
        dispatch(
            push({
                pathname: getTransferConfirmationPath(),
                state: { goBackState: ticketPath },
            }),
        );
        dispatch(
            formActions.signTransactionData({
                idTransaction: displaySummary.idTransaction,
                idActivity,
                idForm: "",
                content: <TransferSummaryData signature isDesktop={isDesktop} />,
                title: resolveTransferTitleKey(displaySummary.idActivity),
                pendingDispatch: displaySummary.pendingDispatch,
            }),
        );
        dispatch(
            formActions.signTransactionPreview({
                idActivity,
                idTransaction: displaySummary.idTransaction,
                idForm: "",
                ticketData: displaySummary,
            }),
        );
        loadTransferForAction("sign");
    }, [dispatch, displaySummary, isDesktop, resolveTicketPath, loadTransferForAction]);

    const cancelPendingTransaction = useCallback(() => {
        if (!displaySummary) {
            return;
        }
        const ticketPath = resolveTicketPath();
        dispatch(
            formActions.cancelTransactionData({
                idTransaction: displaySummary.idTransaction,
                content: <TransferSummaryData signature isDesktop={isDesktop} />,
                title: resolveTransferTitleKey(displaySummary.idActivity),
            }),
        );
        dispatch(formActions.cancelTransactionPre({ idActivity: displaySummary.idActivity, idForm: "" }));
        loadTransferForAction("cancel");
        dispatch(
            push({
                pathname: getTransferConfirmationPath(),
                state: { goBackState: ticketPath },
            }),
        );
    }, [dispatch, displaySummary, isDesktop, resolveTicketPath, loadTransferForAction]);

    const goToPendingAuthorizations = useCallback(() => {
        dispatch(push("/pendingTransaction/list"));
        if (!isMvp3PendingTransactionsEnabled()) {
            dispatch(transactionsActions.initPendings());
            dispatch(
                transactionsActions.loadListRequest(
                    {
                        pageNumber: 1,
                        status: "PENDING",
                        filterPendingDispatch: false,
                        functionRequest: "pendingTransactions",
                        idActivity: displaySummary?.idActivity,
                    },
                    true,
                    false,
                ),
            );
        }
    }, [dispatch, displaySummary]);

    const goToHistoricTransactions = useCallback(() => {
        dispatch(transactionsActions.setFilters(null));
        dispatch(push("/transactions/list"));
    }, [dispatch]);

    const isPendingProcessing = useMemo(
        () => (displaySummary ? isTransactionPendingProcessing(displaySummary) : false),
        [displaySummary],
    );

    const showSignButton = useMemo(() => {
        if (!displaySummary || !isPending || isPendingProcessing || displaySummary.control) {
            return false;
        }
        return canUserSignPendingFromPotentialSchemes(displaySummary, loggedUser, potentialSignaturesData);
    }, [displaySummary, isPending, isPendingProcessing, loggedUser, potentialSignaturesData]);

    const navigationButtons = useMemo(() => {
        if (!isFinished) {
            return [];
        }
        const buttons = [
            {
                "data-testid": transfersTestIds.ticket.download,
                onClick: () => handleDownload(false),
                icon: "SendDown",
                label: i18n.get("transfers.ticket.download.v3", "Descargar"),
                loading: exportPdfLoading,
                css: { paddingLeft: 40, paddingRight: 40 },
            },
        ];
        if (canShareOnMobile && !isDesktop) {
            buttons.push({
                "data-testid": transfersTestIds.ticket.share,
                onClick: () => handleDownload(true),
                icon: "Share",
                label: getLabels("global.share"),
                loading: exportPdfLoading,
            });
        }
        return buttons;
    }, [isFinished, handleDownload, exportPdfLoading, getLabels, canShareOnMobile]);

    const footerNavigationButtons = useMemo(() => {
        if (!displaySummary) {
            return [];
        }

        const homePath = getPostLoginHomePath(isUxDashboardImprovementsEnabled());

        if (isPending) {
            const buttons = [
                {
                    "data-testid": transfersTestIds.ticket.gotoPendings,
                    onClick: goToPendingAuthorizations,
                    icon: "WritingPending",
                    label: getLabels("goto.pendings.transaction.autorizaciones"),
                    variant: "outline",
                },
            ];
            if (!isPendingProcessing && !displaySummary.control) {
                buttons.push({
                    "data-testid": transfersTestIds.ticket.delete,
                    onClick: cancelPendingTransaction,
                    icon: "Trash",
                    label: getLabels("transaction.delete"),
                    variant: "outline",
                });
            }
            // Legacy hides "modify" while the transaction is being processed and when it only
            // cancels a scheduled transfer.
            if (!isPendingProcessing && !displaySummary.idTransactionToCancel) {
                buttons.push({
                    "data-testid": transfersTestIds.ticket.modify,
                    onClick: () => setIsModalDisplayed(true),
                    icon: "Pencil",
                    label: getLabels("global.modify"),
                    variant: "outline",
                });
            }
            if (showSignButton) {
                buttons.push({
                    "data-testid": transfersTestIds.ticket.sign,
                    onClick: signTransaction,
                    icon: "AddServices",
                    label: getLabels("forms.signTransaction.link"),
                    variant: "solid",
                });
            }
            return buttons;
        }

        const buttons = [];
        if (isScheduled && activeEnvironment?.permissions?.scheduledTransactionsCancel) {
            buttons.push({
                "data-testid": transfersTestIds.ticket.cancelScheduled,
                onClick: () => dispatch(push("/transfer/scheduled/cancel")),
                icon: "Trash",
                label: getLabels("scheduler.cancel.button"),
                variant: "outline",
            });
        } else {
            buttons.push({
                "data-testid": transfersTestIds.ticket.gotoHistoric,
                onClick: goToHistoricTransactions,
                icon: "Refund",
                label: getLabels("goto.historic.transaction"),
                variant: "outline",
            });
        }
        buttons.push({
            "data-testid": transfersTestIds.ticket.gotoHome,
            onClick: () => dispatch(push(homePath)),
            icon: "Home",
            label: getLabels("global.goToHome"),
            variant: "solid",
        });
        return buttons;
    }, [
        displaySummary,
        isPending,
        isPendingProcessing,
        isScheduled,
        activeEnvironment,
        dispatch,
        getLabels,
        goToPendingAuthorizations,
        goToHistoricTransactions,
        cancelPendingTransaction,
        showSignButton,
        signTransaction,
    ]);

    if (!displaySummary?.idTransaction) {
        return null;
    }

    return (
        <TransferTicket
            goBack={handleGoBack}
            getLabels={getLabels}
            transferData={transferData}
            title={ticketTitle}
            titleHintText={titleHintText}
            formatOperationDate={formatLegacyWebDateTimeString}
            transactionTicketProps={{
                formatSignatureDate: formatLegacyWebDateTimeString,
                // Banner: UI-kit maps idActivity → base i18n key (…CMF). Override with V3 copy.
                titleTransaction: ticketTitle,
            }}
            preparedBy={preparedBy}
            signedBy={signedBy}
            notes={ticketNotes}
            navigationButtons={navigationButtons}
            footerNavigationButtons={footerNavigationButtons}
            potentialSignaturesData={potentialSignaturesData ?? undefined}
            signatureSchemeDisplayMode={administrationScheme === "medium" ? "medium" : "advanced"}
            openSignatureSchemeMobile={signaturesDrawerOpenMobile}
            onCreateFrequentDestination={showFrequentDestinationButton ? handleCreateFrequentDestination : undefined}>
            <section data-testid={transfersTestIds.ticket.modifyModal}>
                <ModifyPendingTransactionModal
                    isDesktop={isDesktop}
                    isDisplayed={isModalDisplayed}
                    handleDismiss={() => setIsModalDisplayed(false)}
                    dispatch={dispatch}
                    idTransaction={displaySummary.idTransaction}
                    cancelTransactionData={() => <TransferSummaryData signature isDesktop={isDesktop} />}
                />
            </section>
        </TransferTicket>
    );
}

TransferTicketV3Page.propTypes = {
    dispatch: func.isRequired,
    summary: shape({}),
    accounts: arrayOf(shape({})),
    internalTransfer: bool,
    transferGroup: string,
    scheduledCancel: bool,
    isCVU: bool,
    activeEnvironment: shape({}),
    fullName: string,
    lastHrefPathname: string,
    transactionRoute: string,
    isDesktop: bool.isRequired,
    loggedUser: shape({}),
    potentialSignaturesData: arrayOf(shape({})),
    administrationScheme: string,
    frequentDestination: shape({}),
    idEnvironment: string,
};

TransferTicketV3Page.defaultProps = {
    summary: null,
    accounts: [],
    internalTransfer: false,
    transferGroup: "",
    scheduledCancel: false,
    isCVU: false,
    activeEnvironment: null,
    fullName: "",
    lastHrefPathname: "",
    transactionRoute: "",
    loggedUser: null,
    potentialSignaturesData: null,
    administrationScheme: "advanced",
    frequentDestination: null,
    idEnvironment: "",
};

const mapStateToProps = (state) => ({
    summary: transferSelectors.getSummary(state),
    accounts: transferSelectors.getAccounts(state),
    internalTransfer: transferSelectors.getInternalTransfer(state),
    transferGroup: transferSelectors.getTransferGroup(state),
    scheduledCancel: transferSelectors.getScheduledCancel(state),
    isCVU: transferSelectors.getIsCVU(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    fullName: sessionSelectors.getUser(state)?.userFullName,
    lastHrefPathname: statusSelectors.getPathname(state),
    transactionRoute: transactionsSelectors.getTransactionRoute(state),
    loggedUser: sessionSelectors.getUser(state),
    potentialSignaturesData: transactionsSelectors.getPotentialSignaturesData(state),
    administrationScheme: sessionSelectors.getAdministrationScheme(state),
    frequentDestination: frequentDestinationSelectors.getFrequentDestination(state),
    idEnvironment: frequentDestinationSelectors.getEnviromentId(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(TransferTicketV3Page);
